/**
 * TransferOwnershipModal — "Transfer ownership" (wireframe admin-console-wireframe; Ownership design, part 4), kept
 * short and evenly spaced: "Move N JDs from A, B:" over the JDs (one line each), the New owner dropdown over the whole HR
 * pool (greyed out with the reason when someone can't take them) with "X of Y people can take these JDs", the one
 * "Shares and delegations" choice (only when a JD has a delegate or shared people; Carry over preselected; the same
 * answer goes as carry_delegation and carry_share), and Cancel / "Transfer N JDs" side by side.
 *   open, rows (the selected JDs), candidates (GET /ownership/transfer_candidates), loadingCandidates, transferring
 *   onCancel(), onTransfer({ newOwner, carry })   carry: true to carry shares and delegations over
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'antd';
import { SbButton, SbModal } from 'uiCore';
import {
  CARRY, TRANSFER_TITLE, countPeople, getAffectedNames, getEligibleHelp, getMoveIntro, getOwnerNames,
  getTransferButton,
} from 'Utils/ownershipTransfer';
import { PickerLabelStyled } from 'Components/JdOwnership/PersonPickerModal.styles';
import {
  ConflictItemMetaStyled, ConflictProjectItemStyled, ConflictProjectNameStyled,
} from 'Components/EmployeeFunctionAccess/EmployeeFunctionAccess.styles';
import { CarryChoice, NewOwnerField } from './TransferParts';
import { ItemLineStyled, TransferItemsListStyled } from './OwnershipTransfer.styles';

const SHOWN = 5;

const TransferOwnershipModal = ({
  open, rows = [], candidates = null, loadingCandidates = false, candidatesError = null, transferring = false, onCancel,
  onTransfer,
}) => {
  const [newOwner, setNewOwner] = useState(null);
  const [carry, setCarry] = useState(CARRY);
  useEffect(() => {
    if (!open) return;
    setNewOwner(null);
    setCarry(CARRY);
  }, [open]);

  const n = rows.length;
  const owners = getOwnerNames(rows);
  const { ok, total } = countPeople(candidates);
  return (
    <SbModal
      open={open}
      title={TRANSFER_TITLE}
      onCancel={transferring ? undefined : onCancel}
      maskClosable={false}
      destroyOnClose
      width={620}
      // Cancel and the action side by side, as the JD page's people pickers (the action is disabled until someone is
      // picked)
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={transferring} onClick={onCancel}>Cancel</SbButton>,
        <SbButton key="ok" buttonType="primary" disabled={!newOwner || transferring} loading={transferring}
          onClick={() => onTransfer({ newOwner, carry: carry === CARRY })}>
          {getTransferButton(n)}
        </SbButton>,
      ]}
    >
      <Flex vertical gap={16}>
        <div>
          <PickerLabelStyled>{getMoveIntro(n, owners)}</PickerLabelStyled>
          <TransferItemsListStyled data-testid="transfer-items">
            {rows.slice(0, SHOWN).map((r) => (
              // one line per JD: its title, and its Role ID at the right
              <ConflictProjectItemStyled key={r.job_id}>
                <ItemLineStyled justify="space-between" align="center" gap={12}>
                  <ConflictProjectNameStyled>{r.title || r.sb_requisition_number}</ConflictProjectNameStyled>
                  <ConflictItemMetaStyled>{r.sb_requisition_number || r.job_id}</ConflictItemMetaStyled>
                </ItemLineStyled>
              </ConflictProjectItemStyled>
            ))}
            {n > SHOWN && <ConflictProjectItemStyled>and {n - SHOWN} more</ConflictProjectItemStyled>}
          </TransferItemsListStyled>
        </div>
        <NewOwnerField candidates={candidates} rows={rows} loading={loadingCandidates} value={newOwner}
          onChange={setNewOwner} disabled={transferring} error={candidatesError}
          helpBelow={candidates ? getEligibleHelp(ok, total, n) : null} />
        <CarryChoice value={carry} onChange={setCarry} affectedNames={getAffectedNames(rows)} />
      </Flex>
    </SbModal>
  );
};

TransferOwnershipModal.propTypes = {
  open: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  candidates: PropTypes.shape({ candidates: PropTypes.array, excluded: PropTypes.array, total: PropTypes.number }),
  loadingCandidates: PropTypes.bool,
  candidatesError: PropTypes.string,
  transferring: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
};

export default TransferOwnershipModal;
